import { zodResolver } from "@hookform/resolvers/zod";
import type { AxiosError } from "axios";
import axios from "axios";
import { useContext, useState } from "react";
import { Controller, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  DeleteButton,
  ButtonContainer,
  PrimaryButtonLargeV2,
  SecondaryButtonMediumV2,
  TertiaryButtonMediumV2,
} from "../../../../components/Buttons/Buttons";
import { PlusIcon } from "../../../../components/Icons/Icons";
import { Notifications } from "../../../../components/Notifications/NotificationsContext";
import { endpoints } from "../../../../endpoints";
import { Form } from "../../../../layout/FormLayout";
import { FullWidthSelectWithDeleteButton } from "../../../admin/SellerAdmin/SellerAdminTeams/SellerAdminCreateNewTeam";
import type { UUID } from "../../../../types/types";
import type { PIMProduct } from "../../../../types/types.PIM";
import { findNonEmptyDuplicateIndexes } from "../../../../util/form.utils";
import {
  toTitleCase,
  useFormWrapper,
  useStoreState,
} from "../../../../util/util";
import {
  zodSelectBoxDefault,
  zodSelectBoxType,
} from "../../../../util/zod.util";
import { MarginBottomH6 } from "../../../admin/SellerAdmin/PIM/SellerAdminPIMAttributes/CreateAttribute";
import { SearchSelectInfiniteScroll } from "../../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";

const AddCustomerSchema = z.object({
  first_customer: zodSelectBoxType,
  collection: z
    .object({
      customer: zodSelectBoxType,
    })
    .array()
    .optional(),
});

const AddCustomerSchemaFn = (t: (s: string) => string, customers: string[]) =>
  z
    .object({
      first_customer: zodSelectBoxDefault(t).refine(
        ({ value }) =>
          findNonEmptyDuplicateIndexes([value, ...customers]).length === 0,
        t("Duplicate customers not allowed")
      ),
      collection: z
        .object({
          customer: zodSelectBoxDefault(t),
        })
        .array()
        .optional(),
    })
    .superRefine(({ first_customer, collection }, ctx) => {
      const duplicateIdxs = collection
        ? findNonEmptyDuplicateIndexes([
            first_customer.value,
            ...customers,
            ...collection.map(({ customer: { value } }) => value),
          ])
        : [];
      if (duplicateIdxs.length > 0) {
        duplicateIdxs.forEach((idx) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("Duplicate customers not allowed"),
            path: [`collection[${idx - (customers.length + 1)}].customer`],
          });
        });
      }
    });

type FormOutput = z.infer<typeof AddCustomerSchema>;

export const AddCustomer = ({
  product: { id: productId },
  onSuccess,
  existingCustomers,
  onCancel,
}: {
  product: PIMProduct;
  onSuccess: () => Promise<void>;
  existingCustomers: string[];
  onCancel: () => void;
}) => {
  const [loading, setLoading] = useState(false);

  const { tenant_id, storefront_id } = useStoreState();

  const { notifySuccess, notifyError } = useContext(Notifications);

  const { t } = useTranslation();

  const methodsOfUseForm = useFormWrapper({
    resolver: zodResolver(AddCustomerSchemaFn(t, existingCustomers)),
  });

  const { handleSubmit, control, formState, errors } = methodsOfUseForm;

  const { fields, remove, append } = useFieldArray({
    control,
    name: "collection",
  });

  const createFields = (): JSX.Element[] => {
    return fields.map((field, index: number) => (
      <FullWidthSelectWithDeleteButton key={field.id}>
        <Controller
          key={field.id}
          as={SearchSelectInfiniteScroll}
          control={control}
          name={`collection[${index}].customer`}
          defaultValue={{ label: "", value: "" }}
          placeholder={t("Search by company name or primary contact email id")}
          baseUrl={endpoints.v2_storefronts_id_tenants_id_tenants_basic(
            storefront_id,
            tenant_id
          )}
          params={
            new URLSearchParams({
              order_by: "asc",
            })
          }
          getOptions={(response: { name: string; id: UUID }[]) =>
            response.map((tenant) => ({
              label: toTitleCase(tenant.name),
              value: tenant.id,
            }))
          }
          errors={{
            [`collection[${index}].customer`]:
              errors?.collection?.[index]?.customer?.value ??
              errors?.collection?.[index]?.customer ??
              undefined,
          }}
          formState={formState}
          defaultOptions
        />
        <DeleteButton
          testid={`delete-button-${index}`}
          onClick={() => remove(index)}
          type="button"
          height={20}
          width={20}
        />
      </FullWidthSelectWithDeleteButton>
    ));
  };

  const onSubmit = async ({
    collection: otherCustomers,
    first_customer,
  }: FormOutput) => {
    const customers = otherCustomers
      ? [
          first_customer.value,
          ...otherCustomers.map(({ customer: { value } }) => value),
        ]
      : [first_customer.value];
    setLoading(true);
    try {
      await axios.patch<PIMProduct>(
        endpoints.v2_tenants_id_products_id_privacy(tenant_id, productId),
        {
          is_accessible_by_all_customers_and_distributors: false,
          tenants_to_add: customers,
        }
      );
      notifySuccess(t("Customer(s) added successfully"));
      await onSuccess();
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("There was an error modifying the collection"),
        {
          error,
        }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <MarginBottomH6>{t("Search for your customers")}</MarginBottomH6>
        <div style={{ width: "470px" }}>
          <Controller
            as={SearchSelectInfiniteScroll}
            control={control}
            name="first_customer"
            placeholder={t(
              "Search by company name or primary contact email id"
            )}
            defaultValue={{ label: "", value: "" }}
            baseUrl={endpoints.v2_storefronts_id_tenants_id_tenants_basic(
              storefront_id,
              tenant_id
            )}
            params={
              new URLSearchParams({
                order_by: "asc",
              })
            }
            getOptions={(response: { name: string; id: UUID }[]) =>
              response.map((tenant) => ({
                label: toTitleCase(tenant.name),
                value: tenant.id,
              }))
            }
            errors={errors}
            formState={formState}
            defaultOptions
          />
        </div>
        {createFields()}
        <SecondaryButtonMediumV2 onClick={() => append({ name: "" })}>
          <PlusIcon />
          {t("Add Company")}
        </SecondaryButtonMediumV2>
        <ButtonContainer style={{ marginTop: "30px" }}>
          <TertiaryButtonMediumV2 style={{ width: "100%" }} onClick={onCancel}>
            {t("Cancel")}
          </TertiaryButtonMediumV2>
          <PrimaryButtonLargeV2 fullWidth loading={loading} type="submit">
            {t("Add")}
          </PrimaryButtonLargeV2>
        </ButtonContainer>
      </Form>
    </>
  );
};
